import { TrackServiceType } from "./TrackFinancialModels";
import { Country, TrackReleaseSet, TrackServiceModel } from "../../../../../api/useMusicApi";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { InputCheckbox } from "../../../../../components/InputCheckbox";
import { Button } from "../../../../../components/Button";
import { faCheckSquare } from "@fortawesome/free-solid-svg-icons/faCheckSquare";
import { InputDateTimeMoscow } from "../../../../../components/InputDateTimeMoscow";
import _ from "lodash";
import React from "react";
import { TrackFinancialModelApplier } from "./TrackFinancialModelApplier";
import { faSquare } from "@fortawesome/free-regular-svg-icons";

const CountriesCodes = Object.values(Country);
const isServiceCanAddToStoriesOrClips = (service: TrackServiceType) => {
  return service === TrackServiceType.STREAMING;
};

type Props = {
  isDraft: boolean;
  serviceType: TrackServiceType;
  contentType: "audio" | "video";
  service: TrackServiceModel;
  onChange: (value: TrackServiceModel, activeService: TrackServiceType) => void;
  onChangeForAll: (field: keyof TrackReleaseSet, value: any) => void;
};

export const TrackFinancialModelServiceSetup: React.FC<Props> = ({
                                                                   isDraft,
                                                                   service,
                                                                   serviceType,
                                                                   contentType,
                                                                   onChange
                                                                 }) => {
  const { t } = useTranslation();
  const meaningFullCountries = Object.keys(service?.countries ?? {}).filter(
    (key) => !_.isEmpty(service?.countries[key as keyof typeof Country])
  );

  return (
    <div className={"flex-col"} style={{ width: "100%" }}>
      {isServiceCanAddToStoriesOrClips(serviceType) && (
        <div className={"flex-col gap-4 ml-1 mb-3"}>
          <InputCheckbox
            disabled={!isDraft || contentType !== "audio"}
            label={t("add_to_stories").toString()}
            checked={service.story}
            onChange={(value) =>
              onChange({ ...service, story: value }, serviceType)
            }
          />
          <InputCheckbox
            disabled={!isDraft || contentType !== "audio"}
            label={t("add_to_clips").toString()}
            checked={service.clip}
            onChange={(value) =>
              onChange({ ...service, clip: value }, serviceType)
            }
          />
        </div>
      )}

      {isDraft &&
        <div className={"flex gap-2 mb-3"}>
          <Button
            label={t("select_all").toString()}
            className={"px-1 py-1 font-normal"}
            icon={faCheckSquare}
            onClick={() => {
              const emptyCountryState = {} as any;
              CountriesCodes.forEach((c) => {
                if (c !== Country.ALL) {
                  emptyCountryState[c] = {};
                  emptyCountryState[c].start = "";
                }
              });
              onChange(
                {
                  ...service,
                  countries: emptyCountryState
                },
                serviceType
              );
            }}
          />
          <Button
            label={t("unselect_all").toString()}
            icon={faSquare}
            className={"px-1 py-1 font-normal"}
            onClick={() => {
              onChange(
                {
                  ...service,
                  countries: {}
                },
                serviceType
              );
            }}
          />
        </div>
      }
      <div className={"m-0 p-0 flex 2xl:flex-col-reverse gap-3"}>
        <div>
          <div className={"bg-gray-100"} style={{ textAlign: "center" }}>
          </div>
          <div>
            {[...CountriesCodes].map((country) => {
              const isActive = meaningFullCountries.includes(country);
              return (
                <div key={`${serviceType}_${country}`} className={"flex align-items-center gap-2"}
                     style={{ height: 35 }}>
                  <div>
                    <div className={"flex justify-content-center"}>
                      <InputCheckbox
                        disabled={!isDraft}
                        checked={isActive}
                        onChange={(value) => {
                          if (!value) {
                            const countries = service.countries;
                            delete countries[country];
                            onChange(
                              {
                                ...service,
                                countries: countries
                              },
                              serviceType
                            );
                          } else {
                            let countries = service.countries;
                            if (country === Country.ALL) {
                              for (const key in countries) {
                                // @ts-ignore
                                delete countries[key];
                              }
                            } else if (Country.ALL in countries) {
                              delete countries[Country.ALL];
                            }

                            onChange(
                              {
                                ...service,
                                countries: {
                                  ...countries,
                                  [country as keyof typeof Country]: {
                                    start: ""
                                  }
                                }
                              },
                              serviceType
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ width: 100 }}>
                    <div>{t(country)}</div>
                  </div>
                  {serviceType !== TrackServiceType.BLOCK && (
                    <div>
                      {isActive && (
                        <InputDateTimeMoscow
                          disabled={!isDraft}
                          value={
                            service.countries[country as keyof typeof Country]
                              ?.start
                          }
                          style={{ fontSize: "14px" }}
                          onChange={(value) => {
                            onChange(
                              {
                                ...service,
                                countries: {
                                  ...service.countries,
                                  [country as keyof typeof Country]: {
                                    start: value
                                  }
                                }
                              },
                              serviceType
                            );
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {isDraft &&
          ![TrackServiceType.BLOCK, TrackServiceType.BUNDLE].includes(
            serviceType
          ) && (
            <TrackFinancialModelApplier
              activeService={serviceType}
              activeServiceModel={service}
              onChange={onChange}
            />
          )}
      </div>
    </div>
  );
};
